import { headersWithAccessToken } from '../services/authenticatedFetch';
import { API_BASE_URL } from '../config/api';
import { getProductContext } from './quoteFlowTelemetry';

const endpoint = `${API_BASE_URL || ''}/api/client-errors`;
const queueKey = 'tilbudgivern:client-errors:v1';
let rawFetch;
let installed = false;
let sending = false;
let queue = [];
let action = '';
const recent = new Map();
export const redactErrorText = value => String(value ?? '')
  .replace(/Bearer\s+[^\s"']+/gi, '[redacted authorization]')
  .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[redacted]')
  .replace(/((?:password|token|secret|api[_-]?key)\s*[=:]\s*)[^\s&,;]+/gi, '$1[redacted]')
  .slice(0, 3000);
const pathOnly = value => { try { return new URL(typeof value === 'string' ? value : value?.url, window.location.origin).pathname; } catch (_) { return '/'; } };
const isLogging = value => pathOnly(value).startsWith('/api/client-errors');
const storeQueue = () => { try { sessionStorage.setItem(queueKey, JSON.stringify(queue)); } catch (_) {} };
export async function flushClientErrors() {
  if (sending || !rawFetch || !queue.length) return;
  sending = true;
  try {
    while (queue.length) {
      let token; try { token = localStorage.getItem('accessToken'); } catch (_) {}
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      let response;
      try { response = await rawFetch(endpoint, {
        method: 'POST', headers: headersWithAccessToken({ 'Content-Type': 'application/json' }, token),
        body: JSON.stringify(queue[0]), signal: controller.signal
      }); } finally { clearTimeout(timeout); }
      if (!response.ok) break;
      queue.shift(); storeQueue();
    }
  } catch (_) { /* Retry independently; logging must never break the user's action. */ }
  finally { sending = false; }
}
export function logCustomError(message, details = {}) {
  if (!message) return;
  const context = getProductContext();
  const entry = {
    eventId: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    occurredAt: new Date().toISOString(), message: redactErrorText(message),
    errorType: String(details.errorType || 'UserVisibleError').slice(0, 80),
    severity: details.severity === 'warning' ? 'warning' : 'error',
    url: pathOnly(window.location.href),
    projectId: Number(details.projectId ?? context.projectId) || null,
    viewId: String(context.viewId || '').slice(0, 80), stepId: String(context.stepId || '').slice(0, 80),
    action: redactErrorText(details.action || action).slice(0, 120),
    endpoint: details.endpoint ? pathOnly(details.endpoint) : undefined,
    statusCode: Number(details.statusCode) || undefined,
    field: String(details.field || '').slice(0, 120),
    stack: details.stack ? redactErrorText(details.stack).slice(0, 1500) : undefined,
    build: document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src') || 'development'
  };
  const key = JSON.stringify([entry.message, entry.projectId, entry.viewId, entry.stepId, entry.action, entry.endpoint, entry.errorType]);
  if (Date.now() - (recent.get(key) || 0) < 30000) return;
  recent.set(key, Date.now());
  if (recent.size > 200) recent.delete(recent.keys().next().value);
  queue.push(entry); if (queue.length > 100) queue.shift();
  storeQueue(); void flushClientErrors();
}
export const logReactError = (error, info) => logCustomError(error.message, { errorType: 'ReactError', stack: `${error.stack || ''}\n${info?.componentStack || ''}` });
export const logSessionError = (errorType, message) => logCustomError(message, { errorType });
export function setupAxiosErrorLogging(axios) {
  return axios.interceptors.response.use(response => response, error => {
    if (!isLogging(error.config?.url) && error.code !== 'ERR_CANCELED') logCustomError(error.response?.data?.error || error.message, {
      errorType: 'HttpError', endpoint: error.config?.url, statusCode: error.response?.status
    });
    return Promise.reject(error);
  });
}
export function installErrorLogging(axios) {
  if (installed) return;
  installed = true;
  rawFetch = window.fetch.bind(window);
  try { const saved = JSON.parse(sessionStorage.getItem(queueKey)); if (Array.isArray(saved)) queue = saved.slice(-100); } catch (_) {}
  window.addEventListener('error', event => logCustomError(event.message || 'Browser resource failed', { errorType: 'JavaScriptError', stack: event.error?.stack }));
  window.addEventListener('unhandledrejection', event => logCustomError(event.reason?.message || String(event.reason), { errorType: 'UnhandledPromiseRejection', stack: event.reason?.stack }));
  window.addEventListener('online', flushClientErrors);
  window.setInterval(flushClientErrors, 15000);
  document.addEventListener('click', event => { const button = event.target.closest?.('button, [role="button"]'); if (button) action = (button.getAttribute('aria-label') || button.textContent || '').trim(); }, true);
  document.addEventListener('invalid', event => logCustomError(event.target.validationMessage, { errorType: 'NativeValidation', field: event.target.name }), true);
  const alert = window.alert.bind(window);
  window.alert = message => { logCustomError(message, { errorType: 'DialogMessage' }); return alert(message); };
  window.fetch = async (...args) => {
    try {
      const response = await rawFetch(...args);
      if (!response.ok && !isLogging(args[0])) {
        let message = `HTTP ${response.status}`;
        try { const data = await response.clone().json(); message = typeof data.error === 'string' ? data.error : typeof data.message === 'string' ? data.message : message; } catch (_) {}
        logCustomError(message, { errorType: 'HttpError', endpoint: args[0], statusCode: response.status });
      }
      return response;
    } catch (error) {
      if (!isLogging(args[0]) && error.name !== 'AbortError') logCustomError(error.message, { errorType: 'NetworkError', endpoint: args[0] });
      throw error;
    }
  };
  if (axios) setupAxiosErrorLogging(axios);
  let timer;
  const observer = new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      document.querySelectorAll('[role="alert"], .error, .error-message, .error-banner, .field-error, .import-error, .lego-error, .lego-field-error, .lego-preview-error, .customer-search-error, .ordrestyring-sync-error, .ordrestyring-sync-inline-error, .MuiFormHelperText-root.Mui-error').forEach(node => {
        if (node.hidden || getComputedStyle(node).display === 'none' || node.closest('[hidden]')) return;
        if (/success|info/.test(node.className || '')) return;
        const message = node.textContent?.trim();
        if (message) logCustomError(message, { errorType: 'VisibleMessage' });
      });
    }, 150);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'hidden', 'role'] });
  void flushClientErrors();
}
const errorLogger = { logCustomError, logReactError, logSessionError, setupAxiosErrorLogging, installErrorLogging };
export default errorLogger;
